import { Injectable } from '@angular/core';
import { Unsubscribe } from 'firebase/firestore';
import { BehaviorSubject, Subject } from 'rxjs';
import { CrmThread } from '../../modules/messaging/models/crm-thread.model';
import { CrmMessagingService } from '../../modules/messaging/services/crm-messaging.service';
import { MessagingService } from '../../modules/messaging/services/messaging.service';
import { Thread } from '../../modules/messaging/models/thread.model';
import { DirectMessageAuthService } from './direct-message-auth.service';
import { MessagingPermissionsService } from '../../modules/messaging/services/messaging-permissions.service';

export interface NewMessageAlert {
  channel: 'chat' | 'email' | 'sms';
  title: string;
  preview: string;
  /** SMS only - the sender's number, so the banner can name the contact
   *  and open their SMS conversation. */
  address?: string;
}

/**
 * App-wide (not scoped to the Messages module) watcher for new inbound
 * messages - drives the side-menu badge, the toast pop-up, and the alert
 * sound, so the admin finds out about a new message even while working
 * somewhere else entirely. Started once from SideBarComponent, which only
 * renders once the admin is authenticated - this Firestore listener then
 * stays alive for the rest of the session instead of tearing down on every
 * route change.
 */
@Injectable({
  providedIn: 'root'
})
export class MessagingNotificationService {
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  private readonly newMessageSubject = new Subject<NewMessageAlert>();
  readonly newMessage$ = this.newMessageSubject.asObservable();

  private started = false;
  private unsubscribeChatThreads?: Unsubscribe;
  private unsubscribeCrmThreads?: Unsubscribe;

  private chatUnread = 0;
  private crmUnread = 0;

  // Baseline unread count per thread as of the last snapshot, so only an
  // *increase* (a genuinely new inbound message) fires a toast/sound -
  // never the first load, and never someone else reading the thread down.
  private lastChatUnreadByThread = new Map<string, number>();
  private lastCrmUnreadByThread = new Map<string, number>();
  private primedChat = false;
  private primedCrm = false;

  constructor(
    private directMessageAuthService: DirectMessageAuthService,
    private messagingService: MessagingService,
    private crmMessagingService: CrmMessagingService,
    private messagingPermissionsService: MessagingPermissionsService
  ) {}

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.directMessageAuthService.ensureSignedIn().subscribe({
      next: (uid) => {
        this.unsubscribeChatThreads = this.messagingService.listThreads(uid, (threads) => {
          this.chatUnread = threads.reduce((sum, t) => sum + (t.unreadCount[uid] ?? 0), 0);
          this.detectNewChatMessages(threads, uid);
          this.recomputeTotal();
        });
      },
      error: () => {
        // Same as MessagingComponent - if Firebase auth fails, just leave
        // the chat side of the badge/toast at 0 rather than surfacing it.
      }
    });

    this.messagingPermissionsService.getMine().subscribe({
      next: (permissions) => {
        // A user without messaging.view gets no email/SMS toast, sound, or
        // badge contribution - the listener itself never starts, so there's
        // nothing to accidentally leak through a later code path either.
        if (permissions['messaging.view']) this.startCrmListener();
      },
      error: () => this.startCrmListener()
    });
  }

  private startCrmListener(): void {
    // Unread threads only - crm_threads also holds the full HubSpot email
    // history, which every admin page would otherwise download.
    this.unsubscribeCrmThreads = this.crmMessagingService.listUnreadThreads((threads) => {
      this.crmUnread = threads.reduce((sum, t) => sum + (t.unreadCount ?? 0), 0);
      this.detectNewCrmMessages(threads);
      this.recomputeTotal();
    });
  }

  private recomputeTotal(): void {
    this.unreadCountSubject.next(this.chatUnread + this.crmUnread);
  }

  private detectNewChatMessages(threads: Thread[], myUid: string): void {
    if (!this.primedChat) {
      threads.forEach((t) => this.lastChatUnreadByThread.set(t.id, t.unreadCount[myUid] ?? 0));
      this.primedChat = true;
      return;
    }

    threads.forEach((t) => {
      const current = t.unreadCount[myUid] ?? 0;
      const previous = this.lastChatUnreadByThread.get(t.id) ?? 0;
      this.lastChatUnreadByThread.set(t.id, current);

      if (current > previous && t.lastMessage) {
        const senderName = t.participantsInfo?.[t.lastMessage.senderId]?.name;
        this.newMessageSubject.next({
          channel: 'chat',
          title: senderName ?? 'New chat message',
          preview: t.lastMessage.text
        });
      }
    });
  }

  private detectNewCrmMessages(threads: CrmThread[]): void {
    if (!this.primedCrm) {
      threads.forEach((t) => this.lastCrmUnreadByThread.set(t.id, t.unreadCount ?? 0));
      this.primedCrm = true;
      return;
    }

    // A thread that left the unread-only listener has been read - forget
    // its old count, so its next new message toasts again.
    const present = new Set(threads.map((t) => t.id));
    this.lastCrmUnreadByThread.forEach((_, id) => { if (!present.has(id)) this.lastCrmUnreadByThread.set(id, 0); });

    threads.forEach((t) => {
      const current = t.unreadCount ?? 0;
      const previous = this.lastCrmUnreadByThread.get(t.id) ?? 0;
      this.lastCrmUnreadByThread.set(t.id, current);

      if (current > previous && t.lastMessage) {
        this.newMessageSubject.next({
          channel: t.channel,
          title: t.subject || t.externalAddress,
          preview: t.lastMessage.text,
          address: t.channel === 'sms' ? t.externalAddress : undefined
        });
      }
    });
  }
}
